import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Billing, Offer } from '../api/types';
import { prefsChanged, type RevenueUnit } from './prefsSlice';
import { readJson, STORAGE_KEYS } from './storage';

/**
 * Global filters (header toolbar). `from`/`to` are "yyyy-MM"; null means "default range ending at
 * the current month" so the filters stay valid as months pass.
 */
export interface FiltersState {
  from: string | null;
  to: string | null;
  compare: boolean;
  offer: Offer | 'all';
  billing: Billing | 'all';
  unit: RevenueUnit;
}

export const DEFAULT_FILTERS: FiltersState = {
  from: null,
  to: null,
  compare: true,
  offer: 'all',
  billing: 'all',
  unit: 'MRR',
};

/**
 * The saved range, compare switch and unit; the offer and billing filters always start at "all", so
 * a narrowing chosen earlier (or set by a link) never silently carries over to the next visit.
 */
export const loadFilters = (unit: RevenueUnit): FiltersState => ({
  ...readJson(STORAGE_KEYS.filters, { ...DEFAULT_FILTERS, unit }),
  offer: DEFAULT_FILTERS.offer,
  billing: DEFAULT_FILTERS.billing,
});

export const filtersSlice = createSlice({
  name: 'filters',
  initialState: DEFAULT_FILTERS,
  reducers: {
    rangeSet(state, action: PayloadAction<{ from: string; to: string }>) {
      state.from = action.payload.from;
      state.to = action.payload.to;
    },
    compareSet(state, action: PayloadAction<boolean>) {
      state.compare = action.payload;
    },
    unitSet(state, action: PayloadAction<RevenueUnit>) {
      state.unit = action.payload;
    },
    offerSet(state, action: PayloadAction<FiltersState['offer']>) {
      state.offer = action.payload;
    },
    billingSet(state, action: PayloadAction<FiltersState['billing']>) {
      state.billing = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Profile → Preferences sets the defaults; changing them resets saved filters.
    builder.addCase(prefsChanged, (state, action) => {
      if (action.payload.unit !== undefined || action.payload.range !== undefined) {
        return { ...DEFAULT_FILTERS, unit: action.payload.unit ?? state.unit };
      }
      return state;
    });
  },
  selectors: {
    selectFilters: (state) => state,
  },
});

export const { rangeSet, compareSet, unitSet, offerSet, billingSet } = filtersSlice.actions;
export const { selectFilters } = filtersSlice.selectors;
